import { useRef } from "react";

import { CandidacyStevieCallSection } from "./CandidacyStevieCallSection";
import { StevieCallBreakdown } from "./stevie/StevieCallBreakdown";
import { StevieCallRecommendation } from "./StevieCallRecommendation";
import { KickoffAiCallActions } from "@jobs/candidate-sheet-v2/records/KickoffAiCallActions";

import { Column, LoadingIndicator } from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";

import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { STEVIE_TAB_JOB_ID } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { VoiceAIOutreachStatus } from "@/types";

export const StevieCallsTab = ({
  candidateFirstName,
}: {
  candidateFirstName: string;
}) => {
  /* Context */
  const { candidacyId, contentPaddingClassName, isReadOnly, jobId, workerId } =
    useUserSheet();

  /* Database CRUD Operations */
  // Read
  const callHistoryQuery = useGetCallHistory(workerId);

  /* React Hooks */
  // Owned here rather than inside the call card so the breakdown's citations
  // can seek the same player.
  const audioRef = useRef<HTMLAudioElement>(null);

  /* Helpers */
  const aiCalls = callHistoryQuery.data?.aiCalls ?? [];
  // Re-screens can produce several; the newest one is the one on display.
  const currentCall = aiCalls
    .filter(
      (call) =>
        call.candidacyId === candidacyId &&
        call.status === VoiceAIOutreachStatus.COMPLETED.toString(),
    )
    .sort(
      (a, b) =>
        new Date(b.scheduledDateTime).getTime() -
        new Date(a.scheduledDateTime).getTime(),
    )
    .at(0);
  const structuredSummary = currentCall?.callStructuredSummary;
  // The Submit / Do Not Submit badge is a product statement, not a display of
  // call data, so it stays with the pilot job until it is signed off for
  // every role. The breakdown itself is general.
  const showRecommendation = jobId === STEVIE_TAB_JOB_ID;

  /* Render */
  const render = () => {
    if (callHistoryQuery.isPending) {
      return (
        <Column className={cn("py-7", contentPaddingClassName)}>
          <LoadingIndicator variant="medium" />
        </Column>
      );
    }

    return (
      <Column className={cn("mb-12 py-6", contentPaddingClassName)}>
        <CandidacyStevieCallSection
          aiCalls={aiCalls}
          audioRef={audioRef}
          audioRefCallId={currentCall?.id}
          candidateFirstName={candidateFirstName}
          currentCallDetails={
            structuredSummary ? (
              <StevieCallBreakdown
                audioRef={audioRef}
                summary={structuredSummary}
              />
            ) : undefined
          }
          headerActions={
            candidacyId && !isReadOnly ? (
              <KickoffAiCallActions
                candidacyId={candidacyId}
                workerId={workerId}
              />
            ) : null
          }
          isError={callHistoryQuery.isError}
          showPastCalls
          titleActions={
            showRecommendation ? (
              <StevieCallRecommendation hideLearnMore />
            ) : undefined
          }
        />
      </Column>
    );
  };

  return render();
};
